import { useQuery, useQueryClient } from '@tanstack/react-query';
import { RefreshCw, TriangleAlert } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { api, post } from '@/lib/api';
import { keys } from '@/lib/queries';
import { attempt, notify } from '@/lib/notify';
import { confirm } from '@/lib/confirm';
import { ago, LOCALE, plural } from '@/lib/format';
import { cn } from '@/lib/utils';
import type { TabProps } from './FeedPage';

const ACTION_LABEL: Record<string, string> = {
  'feed.create': 'created the calendar', 'feed.update': 'updated settings', 'feed.publish': 'published a version', 'feed.pin': 'rolled back to', 'feed.unpin': 'resumed live updates', 'feed.refresh': 'synced from the source',
  'event.create': 'added an event', 'event.update': 'edited an event', 'event.delete': 'deleted an event', 'event.restore': 'restored an event', 'event.import': 'imported events',
  'include.add': 'added a layer', 'include.remove': 'removed a layer', 'include.reorder': 'reordered layers', 'layers.arrange': 'arranged layers',
  'decision.prefer': 'chose a layer’s version', 'decision.order': 'set a layer preference', 'decision.keep': 'kept both clashing events', 'decision.delete': 'cleared a decision',
  'rule.override': 'changed an event from a layer', 'rule.exclude': 'hid an event from a layer', 'rule.delete': 'undid a change',
  'token.rotate': 'replaced the share link', 'token.reissue': 'issued a new share link', 'member.add': 'added a person', 'member.remove': 'removed a person',
  'snapshot.failed': 'couldn’t build a new version', 'snapshot.empty': 'built an empty version',
};

interface Versions {
  pinned_version: number | null;
  snapshots: { version: number; event_count: number; created_at: number }[];
  audit: { action: string; object: string; detail: string; created_at: number; actor: string | null }[];
}

export function HistoryTab({ id, detail, canEdit }: TabProps) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: [...keys.feed(id), 'versions'], queryFn: () => api<Versions>(`/feeds/${id}/versions`) });
  const owner = detail.role === 'owner';
  const refresh = () => qc.invalidateQueries({ queryKey: keys.feed(id) });
  if (!q.data) return <Skeleton className="h-80 w-full rounded-xl" />;
  const v = q.data;

  async function pin(version: number | null) {
    if (version !== null && !(await confirm({ title: `Roll back to version ${version}?`, body: 'Subscribers will see this version until you resume live updates. Your events and settings aren’t changed.', confirm: 'Roll back' }))) return;
    if (await attempt(() => post(`/feeds/${id}/rollback`, { version }), version === null ? 'Live updates resumed' : `Subscribers now see version ${version}`)) refresh();
  }

  return (
    <div className="grid grid-cols-[minmax(0,1fr)] gap-6">
      {v.pinned_version && owner ? (
        <div className="border-warning/40 bg-warning/10 flex flex-wrap items-center justify-between gap-3 rounded-lg border px-4 py-3 text-sm">
          <span className="flex items-center gap-2">
            <TriangleAlert className="text-warning-foreground size-4" /> Rolled back to version {v.pinned_version}. Subscribers aren’t getting new changes.
          </span>
          <Button size="sm" onClick={() => pin(null)}>
            Resume live updates
          </Button>
        </div>
      ) : null}
      <section className="grid grid-cols-[minmax(0,1fr)] gap-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="max-w-2xl">
            <h2 className="text-lg font-semibold">Published versions</h2>
            <p className="text-muted-foreground text-sm">A new version is built when something changes, the next time a subscriber’s app checks in. If a build ever fails, subscribers keep getting the last good version. The last 10 are kept.</p>
          </div>
          {canEdit ? (
            <Button
              variant="outline"
              onClick={async () => {
                const r = await attempt(() => post<{ version: number; event_count: number }>(`/feeds/${id}/publish`));
                if (r) {
                  notify(`Version ${r.version} · ${plural(r.event_count, 'event')}`);
                  refresh();
                }
              }}
            >
              <RefreshCw /> Publish now
            </Button>
          ) : null}
        </div>
        {v.snapshots.length ? (
          <Card className="gap-0 divide-y py-0">
            {v.snapshots.map((s, i) => {
              const live = !v.pinned_version && i === 0;
              return (
                <div key={s.version} className="flex flex-wrap items-center gap-3 px-4 py-3">
                  <span className="bg-muted rounded-md px-2 py-0.5 font-mono text-xs">v{s.version}</span>
                  <div className="min-w-40 flex-1">
                    <p className="text-sm font-medium">
                      {plural(s.event_count, 'event')} {v.pinned_version === s.version ? <Badge variant="outline" className="border-warning text-warning-foreground ml-1">Pinned</Badge> : live ? <Badge className="ml-1">Live</Badge> : null}
                    </p>
                    <p className="text-muted-foreground text-xs">{new Date(s.created_at).toLocaleString(LOCALE, { dateStyle: 'medium', timeStyle: 'short' })}</p>
                  </div>
                  <Button size="sm" variant="ghost" asChild>
                    <a href={`/v1/feeds/${id}/versions/${s.version}/ics`} target="_blank">
                      View .ics
                    </a>
                  </Button>
                  {owner && v.pinned_version !== s.version && !live ? (
                    <Button size="sm" variant="outline" onClick={() => pin(s.version)}>
                      Roll back
                    </Button>
                  ) : null}
                </div>
              );
            })}
          </Card>
        ) : (
          <p className="text-muted-foreground text-sm">Nothing published yet. The first version is built when someone subscribes, or when you click Publish now.</p>
        )}
      </section>
      <section className="grid grid-cols-[minmax(0,1fr)] gap-3">
        <h2 className="text-lg font-semibold">Activity</h2>
        {v.audit.length ? (
          <Card className="gap-0 py-2">
            <ol className="relative">
              {v.audit.map((a, i) => (
                <li key={i} className="flex gap-3 px-4 py-2.5">
                  <span className={cn('mt-1.5 size-2 shrink-0 rounded-full', a.action.startsWith('snapshot.') ? 'bg-warning' : 'bg-muted-foreground/40')} />
                  <div className="min-w-0 flex-1 text-sm">
                    <p>
                      <span className="font-medium">{a.actor || 'System'}</span> {ACTION_LABEL[a.action] || a.action}
                      {a.action === 'feed.pin' ? ` ${a.object}` : ''}
                    </p>
                    {a.detail ? <p className="text-muted-foreground truncate text-xs">{a.detail}</p> : null}
                  </div>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="text-muted-foreground shrink-0 text-xs whitespace-nowrap">{ago(a.created_at)}</span>
                    </TooltipTrigger>
                    <TooltipContent>{new Date(a.created_at).toLocaleString(LOCALE)}</TooltipContent>
                  </Tooltip>
                </li>
              ))}
            </ol>
          </Card>
        ) : (
          <p className="text-muted-foreground text-sm">No activity yet.</p>
        )}
      </section>
    </div>
  );
}
